import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import ServiceAreaMap from "@/components/ServiceAreaMap";
import {
  Clock,
  ChevronRight,
  Phone,
  Users,
} from "lucide-react";
import { IconMap } from "@/constants/IconMap";

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const HomePage = () => {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-white via-sky-50 to-sky-50 py-20">        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sky-600 font-medium text-sm uppercase tracking-wider">Recognition</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3">
              Record Reader's Choice Awards
            </h2>
            <p className="text-slate-600 mt-4">
              Proud winners for multiple consecutive years
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4">
            {IconMap.AwardPage.awards.map((award) => (
              <div
                key={award.year}
                className="award-badge group cursor-pointer"
                data-testid={`award-${award.year}`}
              >
                <div className="bg-white rounded-xl p-3 shadow-sm border border-slate-100 group-hover:shadow-lg transition-all">
                  <img
                    src={award.image}
                    alt={`Record Reader Award ${award.year}`}
                    className="w-full h-auto rounded-lg"
                  />
                  <p className="text-center mt-2 font-semibold text-slate-700">{award.year}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sky-600 font-medium text-sm uppercase tracking-wider">Find Us</span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3">
                Visit Our Location
              </h2>
              <p className="text-slate-600 mt-4 text-lg">
                Located in the heart of Elmira, we serve customers within a 25km radius.
              </p>

              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Opening Hours</p>
                    <p className="text-slate-600">Open Daily: 6am - 11pm</p>
                    <br />
                    <p className="font-semibold text-slate-900">Attended Service Hours</p>
                    <p className="text-slate-600">Wed & Fri: 10am - 6pm</p>
                    <p className="text-slate-600">Sat & Sun: 10am - 5pm</p>
                    <p className="text-slate-600">Mon, Tue, Thu: Closed</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Address</p>
                    <p className="text-slate-600">7-112 Oriole Parkway</p>
                    <p className="text-slate-600">Elmira, Ontario N3B 1C5</p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Link to="/contact">
                  <Button className="bg-sky-600 hover:bg-sky-700 rounded-full px-8" data-testid="map-book-btn">
                    Book a Pickup
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            <div data-testid="service-area-map">
              <ServiceAreaMap height="450px" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-sky-600 to-sky-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white">
            Ready to Get Started?
          </h2>
          <p className="text-sky-100 mt-4 text-lg">
            Book your first pickup today and experience the Scrubbles difference.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-white text-sky-700 hover:bg-sky-50 rounded-full px-8"
                data-testid="cta-book-btn"
              >
                Book a Pickup
              </Button>
            </Link>
            <a href="tel:519-210-9274">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10 rounded-full px-8"
                data-testid="cta-call-btn"
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
